import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ComponentState, Spinner } from "../components/common";
import { getAppCredentials } from "../configs/utils";
import { USER_PRIVILEGES_STORAGE_KEY, USER_STORAGE_KEY } from "../constants";
import { logout, setAppUser, setPrivileges } from "../store/appSlice";
import parentApi from "../store/parentApi";
import { setStorageItem } from "../utils/localStorage";
import { createPrivilegeObject } from "../utils/privileges/privilegesMapper";
import theme from "../utils/themeVars";
import { getAllApps } from "./appRegistry";
import { t } from "i18next";

const AuthWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const privileges = useSelector((state) => state.app?.privileges);
  const user = useSelector((state) => state.app?.user?.info);
  const tenantDetail = useSelector((state) => state.app?.user?.tenantInfo);
  const [
    getUser,
    { isFetching: isFetchingUser, isError: isErrorUser, error: errorUser },
  ] = parentApi.useLazyGetUserDetailQuery();

  const [
    getPrivileges,
    {
      isFetching: isFetchingPrivileges,
      isError: isErrorPrivileges,
      error: errorPrivileges,
    },
  ] = parentApi.useLazyGetUserPrivilegesQuery();

  const [
    getTenantDetail,
    {
      isFetching: isFetchingTenantDetails,
      isError: isErrorTenantDetails,
      error: errorTenantDetails,
    },
  ] = parentApi.useLazyGetTenantDetailQuery();

  const savePrivileges = (privs, storageOnly) => {
    !storageOnly && dispatch(setPrivileges(createPrivilegeObject(privs)));
    setStorageItem(USER_PRIVILEGES_STORAGE_KEY, privs);
  };

  const renderLogoutButton = () => (
    <Button
      text={t("Logout")}
      onClick={() => dispatch(logout())}
      className="pi-0"
      type="link"
      size="small"
      style={{ "--btn-color": theme["primary-color"] }}
    />
  );

  const fetchInitials = async () => {
    try {
      const [userResp, privResp, tenantDetail] = await Promise.all([
        getUser().unwrap(),
        getPrivileges().unwrap(),
        getTenantDetail().unwrap(),
      ]);
      if (userResp && privResp && tenantDetail) {
        const userDetails = { ...userResp };
        dispatch(
          setAppUser({
            info: userDetails,
            tenantInfo: tenantDetail,
          })
        );
        savePrivileges(privResp?.permissions);
        setStorageItem(USER_STORAGE_KEY, userResp);
      }
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    }
  };

  const onRetry = () => {
    isErrorUser && getUser();
    isErrorPrivileges && getPrivileges();
    isErrorTenantDetails && getTenantDetail();
  };

  useEffect(() => {
    if (!user || !privileges || !tenantDetail) {
      fetchInitials();
    }
  }, []);

  // Show loading while fetching initial data
  if (isFetchingUser || isFetchingPrivileges || isFetchingTenantDetails) {
    return <Spinner size="large" fullPageSpinner />;
  }

  // Show error if any of the initial requests failed
  if (
    (isErrorUser && errorUser) ||
    (isErrorPrivileges && errorPrivileges) ||
    (isErrorTenantDetails && errorTenantDetails)
  ) {
    const errorMessage =
      errorUser?.message ||
      errorPrivileges?.message ||
      errorTenantDetails?.message ||
      t("Failed to load user data");

    return (
      <div
        className="d-grid"
        style={{ height: "100vh", placeContent: "center" }}
      >
        <ComponentState
          status={500}
          title={t("Something went wrong!")}
          subTitle={errorMessage}
          btnLoading={isFetchingUser || isFetchingPrivileges || isFetchingTenantDetails}
          onClick={onRetry}
          buttons={[renderLogoutButton()]}
        />
      </div>
    );
  }

  // Show error if we don't have required data after successful requests
  if (!user) {
    return (
      <div
        className="d-grid"
        style={{ height: "100vh", placeContent: "center" }}
      >
        <ComponentState
          status={403}
          title={t("User Not Found")}
          subTitle={t("Unable to load user information. Please contact your administrator.")}
          buttons={[renderLogoutButton()]}
        />
      </div>
    );
  }

  if (!privileges) {
    return (
      <div
        className="d-grid"
        style={{ height: "100vh", placeContent: "center" }}
      >
        <ComponentState
          status={403}
          title={t("No Privileges")}
          subTitle={t("You don't have sufficient privileges to access this application.")}
          buttons={[renderLogoutButton()]}
        />
      </div>
    );
  }
  return children;
};

export default AuthWrapper;
